import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import { batchGetProducts } from "@dana/Services/DanaProductService";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import {
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import { LoadRealTimeInventory, BatchLoadProductsResult, chain, PopulateApiParameter, ProcessProducts, RequestDataFromApi, UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import { OrderUploadRowError, UploadedItem } from "@insite/client-framework/Store/Components/OrderUpload/OrderUploadState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";

// this will enable HMR for custom handlers
if (module.hot) {
    module.hot.accept();
    const originalChain = chain.slice();
    module.hot.dispose(data => {
        chain.length = 0;
        originalChain.forEach(o => chain.push(o));
    });
}


// trying to reference a handler not in the chain will result in a warning and the chain will not be modified
replaceInChain(
    chain,
    LoadRealTimeInventory,
    props => {

    },
);

// trying to reference a handler not in the chain will result in a warning and the chain will not be modified
replaceInChain(
    chain,
    RequestDataFromApi,
    async props => {

        try {
            props.result.apiResult = await batchGetProducts(props.result.apiParameter);
        } catch (error) {
            const { storefrontAccess } = getSettingsCollection(props.getState()).productSettings;
            if (storefrontAccess === StorefrontAccess.SignInRequiredToBrowse && error.status === 403) {
                // auth timed out with SignInRequiredToBrowsee - throw this up as a 401 so DisplayError reloads the site
                error.status = 401;
                error.errorJson = { message: "error" };
            }

            throw error;
        }

    },
);

// trying to reference a handler not in the chain will result in a warning and the chain will not be modified
replaceInChain(
    chain,
    ProcessProducts,
    props => {

        const state = props.getState();
        const danaGeneralSettings = getDanaGeneralSettings(state);
        const discontinuedLabel = danaGeneralSettings?.partsObsoleteDiscontinuedStatusCode
            ?.toLowerCase()
            .split(",")
            .map((s: any) => s.trim());
        const currentPage = getCurrentPage(state);
        const processState =
            ["VmiBinsPage", "VmiLocationDetailsPage"].indexOf(currentPage.type) !== -1
                ? state.components.vmiBinsImportModal.modalIsOpen
                    ? state.components.vmiBinsImportModal
                    : state.components.vmiCountsImportModal
                : state.components.orderUpload;
        const products = props.result.apiResult;

        if ("uploadCancelled" in processState && processState.uploadCancelled) {
            return false;
        }

        for (let i = 0; i < products.length; i++) {
            const item =
                "uploadedItems" in processState
                    ? processState.uploadedItems[i]
                    : "parsedItems" in processState
                        ? processState.parsedItems[i]
                        : null;

            if (item == null) {
                continue;
            }
            const index = props.parameter.firstRowHeading ? i + 2 : i + 1;
            const product = products[i];
            if (product) {
                const isEmea = useIsEmea(props.getState());
                const error = validateProduct(product, props.parameter.checkInventory, discontinuedLabel || [], item,isEmea);
                if (error === UploadError.None) {
                    product.qtyOrdered = !item.qtyOrdered ? 1 : item.qtyOrdered;
                    addProductToList(product, item, index, props.result, props.parameter.checkInventory);
                } else {
                    props.result.rowErrors.push(mapRowError(index, error, item, product));
                    if (error === UploadError.WrongType) {
                        product.qtyOrdered = !item.qtyOrdered ? 1 : item.qtyOrdered;
                        props.result.products.push(product);
                    }
                }
            } else {
                props.result.rowErrors.push(
                    mapRowError(index, UploadError.NotFound, item, {
                        qtyOrdered: item.qtyOrdered,
                        unitOfMeasureDisplay: item.unitOfMeasure,
                    } as ProductDto),
                );
            }
        }
    }
);


const validateProduct = (product: ProductDto, checkInventory: boolean, discontinuedLabel: string[], uploadedItem: any, isEmea: boolean) => {
    let isAbsolute = false;

    const showAddToCartSection = ('properties' in product) ?
        (product.properties as { danaStatusCode?: string })?.danaStatusCode : "";

    if (showAddToCartSection) {
        isAbsolute = discontinuedLabel?.includes(showAddToCartSection?.toLowerCase());
    }
    if (
        !('properties' in product)
        || isAbsolute
    ) {
        return UploadError.OutOfStock;
    }
    if (
        !('properties' in product) ||
        (product.properties as { showRtbCodeAddToCart?: string })?.showRtbCodeAddToCart !== "true"
        || isAbsolute
    ) {
        return UploadError.Unavailable;
    }

    const requestedShipDate = uploadedItem?.requestedShipDate;
    if (requestedShipDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalize today to midnight for date-only comparison [web:6][cite:2]

        let isValidFormat = false;
        let productDate;
        
        if (isEmea) {
            // Regex for dd-mm-yyyy format [web:4][web:5][web:8]
            const ddmmyyyyRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
            if (ddmmyyyyRegex.test(requestedShipDate)) {
                const [day, month, year] = requestedShipDate.split("-");
                productDate = new Date(Number(year), Number(month) - 1, Number(day));
                if (Number.isFinite(productDate.getTime()) && productDate >= today) { // ESLint-friendly NaN check [cite:3]
                    isValidFormat = true;
                }
            }
        } else {
            // Regex for mm-dd-yyyy format (your original)
            const mmddyyyyRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/;         
            if (mmddyyyyRegex.test(requestedShipDate)) {
                const [month, day, year] = requestedShipDate.split("-");
                productDate = new Date(Number(year), Number(month) - 1, Number(day));
                if (Number.isFinite(productDate.getTime()) && productDate >= today) {
                    isValidFormat = true;
                }
            }
        }

        if (!isValidFormat) {
            return UploadError.WrongType;
        }
    }


    if (product.canConfigure || (product.isConfigured && !product.isFixedConfiguration)) {
        return UploadError.ConfigurableProduct;
    }

    if (product.isStyleProductParent) {
        return UploadError.StyledProduct;
    }

    if (checkInventory && product.qtyOnHand === 0 && product.trackInventory && !product.canBackOrder) {
        return UploadError.OutOfStock;
    }

    if (!product.canAddToCart) {
        return UploadError.Unavailable;
    }


    return UploadError.None;
};

const setProductUnitOfMeasure = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    rowErrors: OrderUploadRowError[],
) => {
    const uoms = getProductUnitOfMeasures(product, item);
    if (uoms.length > 0) {
        const um = uoms[0];
        product.selectedUnitOfMeasure = um.unitOfMeasure;
        product.selectedUnitOfMeasureDisplay = um.unitOfMeasureDisplay;
        product.unitOfMeasure = um.unitOfMeasure;
        product.unitOfMeasureDisplay = um.unitOfMeasureDisplay;

        return false;
    }

    if (item.unitOfMeasure) {
        const errorProduct = mapRowError(index, UploadError.InvalidUnit, item, product);
        errorProduct.umRequested = item.unitOfMeasure;
        rowErrors.push(errorProduct);

        return true;
    }

    return false;
};

const addProductToList = (
    product: ProductDto,
    item: UploadedItem,
    index: number,
    result: BatchLoadProductsResult,
    checkInventory: boolean,
) => {
    const baseUnitOfMeasure = getBaseUnitOfMeasure(product);
    const currentUnitOfMeasure = getCurrentUnitOfMeasure(product);
    if (
        checkInventory &&
        product.trackInventory &&
        !product.canBackOrder &&
        !product.quoteRequired &&
        baseUnitOfMeasure &&
        currentUnitOfMeasure &&
        product.qtyOrdered * baseUnitOfMeasure.qtyPerBaseUnitOfMeasure >
        product.qtyOnHand * currentUnitOfMeasure.qtyPerBaseUnitOfMeasure
    ) {
        const rowError = mapRowError(index, UploadError.NotEnough, item, product);
        result.rowErrors.push(rowError);
    }

    result.products.push(product);
};

const getBaseUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let baseUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.isDefault)[0];
    if (!baseUnitOfMeasure) {
        baseUnitOfMeasure = defaultUnitOfMeasure;
    }

    return baseUnitOfMeasure;
};

const getCurrentUnitOfMeasure = (product: ProductDto) => {
    const defaultUnitOfMeasure = getDefaultProductUnitOfMeasureDto();
    let currentUnitOfMeasure = product.productUnitOfMeasures?.filter(u => u.unitOfMeasure === product.unitOfMeasure)[0];
    if (!currentUnitOfMeasure) {
        currentUnitOfMeasure = defaultUnitOfMeasure;
    }

    return currentUnitOfMeasure;
};

const getDefaultProductUnitOfMeasureDto = () => {
    return {
        productUnitOfMeasureId: "",
        unitOfMeasure: "",
        unitOfMeasureDisplay: "",
        description: "",
        qtyPerBaseUnitOfMeasure: 1,
        roundingRule: "",
        isDefault: false,
        availability: null,
    };
};

const mapRowError = (index: number, errorn: UploadError, item: any, product: any): OrderUploadRowError => {
    const name = item?.name || "";
    const quantity = item?.qtyOrdered || 0;
    const error = errorn as any;
    const requestedShipDate = item?.requestedShipDate || ""; // Default date, can be adjusted as needed
 
    return {
        index,
        error,
        name,
        qtyRequested: quantity,
        umRequested: product.unitOfMeasureDisplay,
        qtyOnHand: product.qtyOnHand,
        requestedShipDate,
        replacementPartNumber:product?.properties?.replacementPartNumber

    } as any;
};

const getProductUnitOfMeasures = (product: ProductDto, item: UploadedItem) => {
    const lowerCaseItemUm = item.unitOfMeasure ? item.unitOfMeasure.toLowerCase() : "";

    return lowerCaseItemUm && product.productUnitOfMeasures
        ? product.productUnitOfMeasures.filter(
            u =>
                u.unitOfMeasure.toLowerCase() === lowerCaseItemUm ||
                u.unitOfMeasureDisplay.toLowerCase() === lowerCaseItemUm ||
                u.description.toLowerCase() === lowerCaseItemUm,
        )
        : [];
};
